import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select,
  message, 
  Typography, 
  Space, 
  Popconfirm,
  Tag,
  Row,
  Col,
  Divider,
  Grid
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { receiversService } from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;
const { useBreakpoint } = Grid;

const Clients = () => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [bottomPaginationVisible, setBottomPaginationVisible] = useState(true);
  const topPaginationRef = useRef(null);
  const [form] = Form.useForm();
  const screens = useBreakpoint();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async (search = '') => {
    setLoading(true);
    try {
      const data = await receiversService.getReceivers(search);
      const list = data.data || [];
      setClients(list.filter((r) => String(r?.use_tax || '').toUpperCase() !== 'CN01'));
    } catch (error) {
      message.error('Error al cargar clientes');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchClients(value);
  };

  const showModal = (client = null) => {
    setEditingClient(client);
    setModalVisible(true);
    if (client) {
      form.setFieldsValue(client);
    } else {
      form.resetFields();
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingClient(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Regla de negocio: Si es RFC genérico nacional o extranjero, Uso CFDI debe ser S01
      const rfc = values.tax_id ? values.tax_id.toUpperCase() : '';
      if (rfc === 'XAXX010101000' || rfc === 'XEXX010101000') {
          values.use_tax = 'S01';
      }

      if (editingClient) {
        await receiversService.updateReceiver(editingClient.id, values);
        message.success('Cliente actualizado exitosamente');
      } else {
        await receiversService.createReceiver(values);
        message.success('Cliente creado exitosamente');
      }
      handleCancel();
      fetchClients(searchText);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al guardar cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await receiversService.deleteReceiver(id);
      message.success('Cliente eliminado exitosamente');
      fetchClients(searchText);
    } catch (error) {
      message.error('Error al eliminar cliente');
    } finally {
      setLoading(false);
    }
  };

  const validateRFC = (_, value) => {
    if (!value) return Promise.resolve();
    
    const rfcPattern = /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/;
    if (!rfcPattern.test(value.toUpperCase())) {
      return Promise.reject(new Error('RFC inválido'));
    }
    return Promise.resolve();
  };

  const columns = [
    {
      title: 'RFC',
      dataIndex: 'tax_id',
      key: 'tax_id',
      width: 160,
      render: (tax_id) => <Text strong>{tax_id}</Text>,
    },
    {
      title: 'Razón Social',
      dataIndex: 'tax_name',
      key: 'tax_name',
      width: 300,
      ellipsis: true,
    },
    {
      title: 'Uso CFDI',
      dataIndex: 'use_tax',
      key: 'use_tax',
      width: 100,
      render: (uso) => (
        <Tag color="blue">{uso}</Tag>
      ),
    },
    {
      title: 'Régimen Fiscal',
      dataIndex: 'fiscal_regimen',
      key: 'fiscal_regimen',
      width: 120,
      render: (regimen) => (
        <Tag color="green">{regimen}</Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            size="small"
          />
          <Popconfirm
            title="¿Estás seguro de eliminar este cliente?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: screens.xs ? '16px' : '24px' }}>
      <Card>
        <div style={{ 
          display: 'flex', 
          flexDirection: screens.md ? 'row' : 'column',
          justifyContent: 'space-between', 
          alignItems: screens.md ? 'center' : 'flex-start',
          gap: '16px',
          marginBottom: '24px' 
        }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              <TeamOutlined style={{ marginRight: '8px' }} />
              Clientes
            </Title>
            <Text type="secondary">
              Gestiona tu catálogo de clientes y receptores
            </Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
            block={!screens.md}
          >
            Nuevo Cliente
          </Button>
        </div>

        <Divider />

        <Row style={{ marginBottom: '16px' }}>
          <Col xs={24} md={12} lg={8}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                placeholder="Buscar clientes..."
                allowClear
                onChange={(e) => {
                  if (!e.target.value) handleSearch('');
                  setSearchText(e.target.value);
                }}
                onPressEnter={() => handleSearch(searchText)}
                style={{ borderRadius: '8px' }}
              />
              <Button 
                type="primary" 
                icon={<SearchOutlined />} 
                onClick={() => handleSearch(searchText)}
                style={{ borderRadius: '8px' }}
              />
            </div>
          </Col>
        </Row>

        <div ref={topPaginationRef} />
        <Table
          columns={columns}
          dataSource={clients}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            position: bottomPaginationVisible ? ['topRight', 'bottomRight'] : ['topRight'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} clientes`,
          }}
        />
      </Card>

      <Modal
        title={editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="tax_id"
                label="RFC"
                rules={[
                  { required: true, message: 'El RFC es requerido' },
                  { validator: validateRFC }
                ]}
              >
                <Input 
                  placeholder="RFC del cliente" 
                  style={{ textTransform: 'uppercase' }}
                  onChange={(e) => {
                    e.target.value = e.target.value.toUpperCase();
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="use_tax"
                label="Uso CFDI"
                rules={[{ required: true, message: 'El uso CFDI es requerido' }]}
              >
                <Select placeholder="Selecciona el uso CFDI">
                  <Option value="G01">G01 - Adquisición de mercancías</Option>
                  <Option value="G02">G02 - Devoluciones, descuentos o bonificaciones</Option>
                  <Option value="G03">G03 - Gastos en general</Option>
                  <Option value="I01">I01 - Construcciones</Option>
                  <Option value="I02">I02 - Mobiliario y equipo de oficina por inversiones</Option>
                  <Option value="I03">I03 - Equipo de transporte</Option>
                  <Option value="I04">I04 - Equipo de computo y accesorios</Option>
                  <Option value="I05">I05 - Dados, troqueles, moldes, matrices y herramental</Option>
                  <Option value="I06">I06 - Comunicaciones telefónicas</Option>
                  <Option value="I07">I07 - Comunicaciones satelitales</Option>
                  <Option value="I08">I08 - Otra maquinaria y equipo</Option>
                  <Option value="D01">D01 - Honorarios médicos, dentales y gastos hospitalarios</Option>
                  <Option value="D02">D02 - Gastos médicos por incapacidad o discapacidad</Option>
                  <Option value="D03">D03 - Gastos funerales</Option>
                  <Option value="D04">D04 - Donativos</Option>
                  <Option value="D05">D05 - Intereses reales efectivamente pagados por créditos hipotecarios</Option>
                  <Option value="D06">D06 - Aportaciones voluntarias al SAR</Option>
                  <Option value="D07">D07 - Primas por seguros de gastos médicos</Option>
                  <Option value="D08">D08 - Gastos de transportación escolar obligatoria</Option>
                  <Option value="D09">D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones</Option>
                  <Option value="D10">D10 - Pagos por servicios educativos (colegiaturas)</Option>
                  <Option value="P01">P01 - Por definir</Option>
                  <Option value="S01">S01 - Sin efectos fiscales</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="tax_name"
            label="Razón Social"
            rules={[{ required: true, message: 'La razón social es requerida' }]}
          >
            <Input placeholder="Razón social del cliente" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="fiscal_regimen"
                label="Régimen Fiscal"
                rules={[{ required: true, message: 'El régimen fiscal es requerido' }]}
              >
                <Select placeholder="Selecciona el régimen fiscal">
                  <Option value="601">601 - General de Ley Personas Morales</Option>
                  <Option value="603">603 - Personas Morales con Fines no Lucrativos</Option>
                  <Option value="605">605 - Sueldos y Salarios e Ingresos Asimilados a Salarios</Option>
                  <Option value="606">606 - Arrendamiento</Option>
                  <Option value="607">607 - Régimen de Enajenación o Adquisición de Bienes</Option>
                  <Option value="608">608 - Demás ingresos</Option>
                  <Option value="610">610 - Residentes en el Extranjero sin Establecimiento Permanente en México</Option>
                  <Option value="611">611 - Ingresos por Dividendos (socios y accionistas)</Option>
                  <Option value="612">612 - Personas Físicas con Actividades Empresariales y Profesionales</Option>
                  <Option value="614">614 - Ingresos por intereses</Option>
                  <Option value="615">615 - Régimen de los ingresos por obtención de premios</Option>
                  <Option value="616">616 - Sin obligaciones fiscales</Option>
                  <Option value="620">620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos</Option>
                  <Option value="621">621 - Incorporación Fiscal</Option>
                  <Option value="622">622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras</Option>
                  <Option value="623">623 - Opcional para Grupos de Sociedades</Option>
                  <Option value="624">624 - Coordinados</Option>
                  <Option value="625">625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas</Option>
                  <Option value="626">626 - Régimen Simplificado de Confianza</Option>
                  <Option value="628">628 - Hidrocarburos</Option>
                  <Option value="629">629 - De los Regímenes Fiscales Preferentes y de las Empresas Multinacionales</Option>
                  <Option value="630">630 - Enajenación de acciones en bolsa de valores</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="tax_location"
                label="Código Postal"
                rules={[
                  { required: true, message: 'El código postal es requerido' },
                  { pattern: /^[0-9]{5}$/, message: 'Debe ser un código postal válido (5 dígitos)' }
                ]}
              >
                <Input placeholder="Código Postal" maxLength={5} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { type: 'email', message: 'Email inválido' },
                  { required: false, message: 'El email es opcional' }
                ]}
              >
                <Input placeholder="email@ejemplo.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="telefono"
                label="Teléfono"
              >
                <Input placeholder="Teléfono del cliente" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Title level={5}>Dirección</Title>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="calle"
                label="Calle"
              >
                <Input placeholder="Calle" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item
                name="numero_exterior"
                label="No. Exterior"
              >
                <Input placeholder="No. Ext." />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item
                name="numero_interior"
                label="No. Interior"
              >
                <Input placeholder="No. Int." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="colonia"
                label="Colonia"
              >
                <Input placeholder="Colonia" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="municipio"
                label="Municipio"
              >
                <Input placeholder="Municipio" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="estado"
                label="Estado"
              >
                <Input placeholder="Estado" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="pais"
                label="País"
                initialValue="México"
              >
                <Input placeholder="País" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancel}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingClient ? 'Actualizar' : 'Crear'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Clients;
